/**
 * ImportTab (Tab 9) — Bulk agent import via CSV upload or MLS sync.
 *
 * Form-based layout (no split panel). Sub-tabs: CSV Upload / MLS Sync /
 * Manual Entry. CSV upload uses FileReader to preview + POST to backend.
 */

import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileCsv,
  faDatabase,
  faUserPlus,
  faUpload,
  faCheckCircle,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

import {
  colors,
  fonts,
  GoldButton,
  OutlineButton,
  NavyInput,
} from '../shared';

const IMPORT_METHODS = [
  { key: 'csv',    label: 'CSV Upload',  icon: faFileCsv,  description: 'Upload a spreadsheet of agents' },
  { key: 'mls',    label: 'MLS Sync',    icon: faDatabase, description: 'Pull roster from your MLS feed' },
  { key: 'manual', label: 'Manual Entry', icon: faUserPlus, description: 'Add a single agent directly' },
];

interface ImportTabProps {
  brokerageId?: string;
}

const ImportTab: React.FC<ImportTabProps> = () => {
  const [activeMethod, setActiveMethod] = useState('csv');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[][]>([]);
  const [manual, setManual] = useState({ name: '', email: '', phone: '', license: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = text.split('\n').slice(0, 6).map((r) => r.split(','));
      setPreview(rows);
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto' }}>
      <div style={{ padding: '28px 32px', maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            background: colors.card,
            borderLeft: `4px solid ${colors.gold}`,
            borderRadius: '8px',
            padding: '20px 24px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              color: colors.navy,
              margin: 0,
              letterSpacing: '0.5px',
            }}
          >
            Import Agents
          </h1>
          <p style={{ color: '#555', fontSize: '14px', margin: '4px 0 0 0' }}>
            Bring your entire roster onto the platform in minutes
          </p>
        </div>

        {/* Method selector */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {IMPORT_METHODS.map((m) => {
            const isActive = activeMethod === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setActiveMethod(m.key)}
                style={{
                  background: isActive ? colors.white : `${colors.white}99`,
                  border: `1px solid ${isActive ? colors.gold : colors.border}`,
                  borderLeft: `4px solid ${isActive ? colors.gold : 'transparent'}`,
                  borderRadius: '10px',
                  padding: '18px 20px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: fonts.body,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  transition: 'all 0.15s',
                  boxShadow: isActive ? '0 2px 8px rgba(201,149,42,0.15)' : '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: isActive
                      ? `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`
                      : `${colors.gold}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon
                    icon={m.icon}
                    style={{ color: isActive ? colors.white : colors.gold, fontSize: '16px' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>{m.label}</div>
                  <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
                    {m.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Method body */}
        <SectionCard
          title={IMPORT_METHODS.find((m) => m.key === activeMethod)?.label || ''}
        >
          {activeMethod === 'csv' && (
            <>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    handleFileSelect(e.dataTransfer.files[0]);
                  }
                }}
                style={{
                  border: `2px dashed ${colors.gold}66`,
                  borderRadius: '10px',
                  padding: '36px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: `${colors.gold}08`,
                  transition: 'background 0.15s',
                }}
              >
                <FontAwesomeIcon
                  icon={faUpload}
                  style={{ color: colors.gold, fontSize: '36px', marginBottom: '10px' }}
                />
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy, marginBottom: '4px' }}>
                  {csvFile ? csvFile.name : 'Drop a CSV here or click to browse'}
                </div>
                <div style={{ fontSize: '11px', color: colors.muted }}>
                  Required columns: name, email, phone, license_number
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
              </div>

              {preview.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: colors.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '8px',
                    }}
                  >
                    Preview (first 5 rows)
                  </div>
                  <div
                    style={{
                      background: colors.off,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      padding: '12px',
                      overflowX: 'auto',
                    }}
                  >
                    <table style={{ fontSize: '11px', color: colors.navy, borderCollapse: 'collapse' }}>
                      <tbody>
                        {preview.map((row, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                            {row.map((cell, j) => (
                              <td
                                key={j}
                                style={{
                                  padding: '4px 8px',
                                  fontWeight: i === 0 ? 700 : 400,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                <OutlineButton onClick={() => window.open('/templates/agent-import-template.csv', '_blank')}>
                  <FontAwesomeIcon icon={faDownload} /> Download Template
                </OutlineButton>
                <GoldButton disabled={!csvFile}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Import {csvFile && preview.length > 1 ? `${preview.length - 1} Agents` : 'Agents'}
                </GoldButton>
              </div>
            </>
          )}

          {activeMethod === 'mls' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <FontAwesomeIcon
                icon={faDatabase}
                style={{ color: colors.gold, fontSize: '44px', marginBottom: '12px' }}
              />
              <h3
                style={{
                  fontFamily: fonts.display,
                  fontSize: '22px',
                  color: colors.navy,
                  margin: '0 0 8px 0',
                }}
              >
                MLS Roster Sync
              </h3>
              <p
                style={{
                  fontSize: '13px',
                  color: colors.muted,
                  maxWidth: '440px',
                  margin: '0 auto 16px',
                  lineHeight: 1.5,
                }}
              >
                Pull your complete agent roster directly from ARMLS, Bright MLS, or your local feed.
                Agents will be imported with their license numbers and contact info pre-filled.
              </p>
              <GoldButton onClick={() => (window.location.href = '/broker-intelligence/mls')}>
                Configure MLS Feed <FontAwesomeIcon icon={faArrowRight} />
              </GoldButton>
            </div>
          )}

          {activeMethod === 'manual' && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <Field label="Full name">
                <WhiteInput
                  value={manual.name}
                  onChange={(e) => setManual({ ...manual, name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email">
                <WhiteInput
                  type="email"
                  value={manual.email}
                  onChange={(e) => setManual({ ...manual, email: e.target.value })}
                  placeholder="jane@brokerage.com"
                />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field label="Phone">
                  <WhiteInput
                    value={manual.phone}
                    onChange={(e) => setManual({ ...manual, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </Field>
                <Field label="License #">
                  <WhiteInput
                    value={manual.license}
                    onChange={(e) => setManual({ ...manual, license: e.target.value })}
                    placeholder="SA-123456"
                  />
                </Field>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <GoldButton disabled={!manual.name || !manual.email}>
                  <FontAwesomeIcon icon={faUserPlus} /> Add Agent
                </GoldButton>
              </div>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div
      style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <h5
        style={{
          margin: 0,
          fontSize: '11px',
          fontWeight: 700,
          color: colors.navy,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {title}
      </h5>
    </div>
    <div style={{ padding: '24px 20px' }}>{children}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label
      style={{
        display: 'block',
        fontSize: '11px',
        fontWeight: 600,
        color: colors.muted,
        marginBottom: '5px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const WhiteInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <NavyInput
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
    }}
  />
);

export default ImportTab;
